import { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import { Modal } from 'react-bootstrap-v5';
import moment from 'moment';
import Swal from 'sweetalert2';
import { IArquivoRetorno } from '../../interfaces/IArquivoRetorno';
import ArquivoRetornoService from '../../services/ArquivoRetornoService';
import { agruparArquivosPorOperacao, baixarGrupoComoZip, GrupoArquivoRetorno } from '../../utils/arquivoRetornoGrupos';

interface Props {
    relatorioId: number;
    show: boolean;
    toggleModal: () => void;
}

function statusInfo(arquivo: IArquivoRetorno): { label: string; color: string; bg: string } {
    if (arquivo.erro)
        return { label: 'ERRO', color: '#d9534f', bg: '#d9534f22' };
    if (arquivo.processando)
        return { label: 'PROCESSANDO', color: '#f0ad4e', bg: '#f0ad4e22' };
    if (arquivo.processou)
        return { label: 'CONCLUÍDO', color: '#5cb85c', bg: '#5cb85c22' };
    return { label: 'AGUARDANDO', color: '#6c757d', bg: '#6c757d22' };
}

function ModalDownloadsCartao({ relatorioId, show, toggleModal }: Props) {
    const [dados, setDados] = useState<IArquivoRetorno[]>([]);
    const [loading, setLoading] = useState(false);
    const [gerando, setGerando] = useState(false);
    const [baixando, setBaixando] = useState<number | null>(null);
    const [baixandoGrupo, setBaixandoGrupo] = useState<number | null>(null);
    const [progressoGrupo, setProgressoGrupo] = useState<{ atual: number; total: number } | null>(null);
    const [pollingInterval, setPollingInterval] = useState<ReturnType<typeof setInterval> | null>(null);

    const grupos = useMemo(() => agruparArquivosPorOperacao(dados), [dados]);

    const buscar = useCallback(async () => {
        if (!relatorioId) return;
        setLoading(true);
        try {
            const res = await ArquivoRetornoService.listarPorCartao(relatorioId);
            setDados(res);
        } catch {
            // silencioso
        } finally {
            setLoading(false);
        }
    }, [relatorioId]);

    useEffect(() => {
        if (show && relatorioId) {
            buscar();
        }
    }, [show, relatorioId, buscar]);

    // Limpa dados ao fechar
    useEffect(() => {
        if (!show) {
            if (pollingInterval) {
                clearInterval(pollingInterval);
                setPollingInterval(null);
            }
        }
    }, [show]);

    // Polling enquanto houver arquivos processando
    useEffect(() => {
        const temProcessando = dados.some(d => d.processando);

        if (temProcessando && show) {
            if (!pollingInterval) {
                const interval = setInterval(buscar, 8000);
                setPollingInterval(interval);
            }
        } else {
            if (pollingInterval) {
                clearInterval(pollingInterval);
                setPollingInterval(null);
            }
        }

        return () => {
            if (pollingInterval) clearInterval(pollingInterval);
        };
    }, [dados, show]);

    const handleGerar = useCallback(async () => {
        if (!relatorioId) return;

        setGerando(true);
        try {
            await ArquivoRetornoService.gerarXlsxCartao(relatorioId);
            Swal.fire({
                text: 'Geração do relatório XLSX iniciada! O arquivo ficará disponível para download quando concluído.',
                icon: 'success',
                timer: 4000,
                showConfirmButton: false
            });
            await buscar();
        } catch {
            Swal.fire({
                text: 'Erro ao iniciar geração do XLSX. Tente novamente.',
                icon: 'error',
                timer: 3000,
                showConfirmButton: false
            });
        } finally {
            setGerando(false);
        }
    }, [relatorioId, buscar]);

    const handleDownload = useCallback(async (arquivo: IArquivoRetorno) => {
        if (!arquivo.processou || arquivo.erro) return;

        setBaixando(arquivo.id);
        try {
            const url = await ArquivoRetornoService.obterUrlDownload(arquivo.id);
            const link = document.createElement('a');
            link.href = url;
            link.download = arquivo.nomeArquivo;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch {
            Swal.fire({
                text: 'Erro ao gerar link de download. Tente novamente.',
                icon: 'error',
                timer: 3000,
                showConfirmButton: false
            });
        } finally {
            setBaixando(null);
        }
    }, []);

    const handleBaixarGrupo = useCallback(async (grupo: GrupoArquivoRetorno) => {
        if (!grupo.operacaoId || !grupo.completo) return;

        setBaixandoGrupo(grupo.operacaoId);
        setProgressoGrupo({ atual: 0, total: grupo.arquivos.filter(a => a.processou && !a.erro).length });
        try {
            const nomeBase = grupo.arquivos[0]?.nomeArquivo.replace(/_parte\d+/i, '').replace(/\.xlsx$/i, '') || 'lancamentos';
            await baixarGrupoComoZip(grupo, `${nomeBase}_completo.zip`, (atual, total) => setProgressoGrupo({ atual, total }));
            if (grupo.temErro) {
                Swal.fire({
                    text: 'Uma ou mais partes falharam e não foram incluídas no zip.',
                    icon: 'warning',
                    timer: 4000,
                    showConfirmButton: false
                });
            }
        } catch {
            Swal.fire({
                text: 'Erro ao baixar as partes do relatório. Tente novamente.',
                icon: 'error',
                timer: 3000,
                showConfirmButton: false
            });
        } finally {
            setBaixandoGrupo(null);
            setProgressoGrupo(null);
        }
    }, []);

    return (
        <Modal size='lg' centered show={show} onHide={toggleModal}>
            <div className='modal-content' style={{ backgroundColor: '#F4F5F7' }}>
                <div className='modal-header'>
                    <h5 className='modal-title titulo-modal'>Downloads do Cartão</h5>
                    <div className='btn btn-icon btn-sm btn-active-light-primary ms-2'>
                        <i onClick={toggleModal} className='fas fa-times' style={{ color: '#5E6397', cursor: 'pointer' }} />
                    </div>
                </div>

                <div className='modal-body'>
                    {/* Ação de gerar XLSX */}
                    <div className='card card-body mb-4 p-3' style={{ backgroundColor: '#fff', border: '1px solid #e4e6ef' }}>
                        <div className='d-flex align-items-center justify-content-between flex-wrap gap-2'>
                            <div>
                                <div className='fw-bold' style={{ fontSize: 13 }}>Relatório de Lançamentos</div>
                                <div className='text-muted' style={{ fontSize: 11 }}>
                                    Exporta todos os lançamentos do cartão em XLSX (até 1 milhão de linhas)
                                </div>
                            </div>
                            <button
                                className='btn btn-sm btn-success d-flex align-items-center gap-2'
                                onClick={handleGerar}
                                disabled={gerando}
                            >
                                {gerando ? (
                                    <span className='spinner-border spinner-border-sm' />
                                ) : (
                                    <i className='fas fa-file-excel' />
                                )}
                                {gerando ? 'Gerando...' : 'Gerar XLSX'}
                            </button>
                        </div>
                    </div>

                    {/* Contagem */}
                    <div className='d-flex justify-content-between align-items-center mb-3'>
                        <span className='fw-semibold' style={{ fontSize: 13 }}>Arquivos gerados</span>
                        <button
                            className='btn btn-sm btn-light-secondary d-flex align-items-center gap-1'
                            onClick={buscar}
                            disabled={loading}
                            style={{ fontSize: 12 }}
                        >
                            <i className='fas fa-sync-alt' style={{ fontSize: 11 }} />
                            Atualizar
                        </button>
                    </div>

                    {/* Tabela */}
                    {loading && dados.length === 0 ? (
                        <div className='d-flex justify-content-center py-5'>
                            <span className='spinner-border spinner-border-sm text-primary me-2 mt-1' />
                            <span>Carregando...</span>
                        </div>
                    ) : dados.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '30px', color: '#6c757d' }}>
                            Nenhum arquivo gerado ainda.
                        </div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-3 mb-0'>
                                <thead>
                                    <tr className='fw-bold text-muted' style={{ fontSize: 12 }}>
                                        <th>Arquivo</th>
                                        <th>Gerado em</th>
                                        <th className='text-center'>Status</th>
                                        <th className='text-center'>Download</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {grupos.map(grupo => (
                                        <Fragment key={grupo.operacaoId ?? `sem-operacao-${grupo.arquivos[0].id}`}>
                                            {grupo.arquivos.length > 1 && (
                                                <tr>
                                                    <td colSpan={4} style={{ backgroundColor: '#eef1f5' }}>
                                                        <div className='d-flex align-items-center justify-content-between flex-wrap gap-2 py-1'>
                                                            <span style={{ fontSize: 11 }}>
                                                                <i className='fas fa-layer-group me-1' />
                                                                Export dividido em {grupo.arquivos.length} partes
                                                                {grupo.temErro && (
                                                                    <span style={{ color: '#d9534f' }} className='ms-2'>(uma ou mais partes falharam)</span>
                                                                )}
                                                            </span>
                                                            <button
                                                                className='btn btn-sm btn-light-primary d-flex align-items-center gap-1'
                                                                disabled={!grupo.completo || baixandoGrupo === grupo.operacaoId}
                                                                onClick={() => handleBaixarGrupo(grupo)}
                                                                style={{ fontSize: 11 }}
                                                            >
                                                                {baixandoGrupo === grupo.operacaoId ? (
                                                                    <>
                                                                        <span className='spinner-border spinner-border-sm' />
                                                                        {progressoGrupo ? `Baixando ${progressoGrupo.atual}/${progressoGrupo.total}...` : 'Baixando...'}
                                                                    </>
                                                                ) : !grupo.completo ? (
                                                                    'Aguardando conclusão de todas as partes...'
                                                                ) : (
                                                                    <>
                                                                        <i className='fas fa-file-archive' />
                                                                        Baixar tudo (.zip)
                                                                    </>
                                                                )}
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                            {grupo.arquivos.map((arquivo, idx) => {
                                                const st = statusInfo(arquivo);
                                                return (
                                                    <tr key={arquivo.id}>
                                                        <td style={{ fontSize: 12, maxWidth: 260 }}>
                                                            <span
                                                                title={arquivo.nomeArquivo}
                                                                style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                            >
                                                                {grupo.arquivos.length > 1 && (
                                                                    <span className='text-muted me-1'>[Parte {idx + 1}]</span>
                                                                )}
                                                                {arquivo.nomeArquivo}
                                                            </span>
                                                        </td>
                                                        <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                                                            {moment(arquivo.dataArquivo).format('DD/MM/YYYY HH:mm')}
                                                        </td>
                                                        <td className='text-center'>
                                                            {arquivo.processando ? (
                                                                <span className='d-flex align-items-center justify-content-center gap-1'>
                                                                    <span className='spinner-border spinner-border-sm' style={{ width: 12, height: 12, borderWidth: 2, color: st.color }} />
                                                                    <span style={{ fontSize: 10, fontWeight: 'bold', color: st.color }}>PROCESSANDO</span>
                                                                </span>
                                                            ) : (
                                                                <span style={{
                                                                    fontSize: 10,
                                                                    fontWeight: 'bold',
                                                                    color: st.color,
                                                                    backgroundColor: st.bg,
                                                                    padding: '2px 8px',
                                                                    borderRadius: 4,
                                                                    whiteSpace: 'nowrap'
                                                                }}>
                                                                    {st.label}
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className='text-center'>
                                                            {arquivo.processou && !arquivo.erro ? (
                                                                <button
                                                                    className='btn btn-icon btn-sm btn-light-success'
                                                                    title='Baixar arquivo'
                                                                    onClick={() => handleDownload(arquivo)}
                                                                    disabled={baixando === arquivo.id}
                                                                >
                                                                    {baixando === arquivo.id ? (
                                                                        <span className='spinner-border spinner-border-sm' />
                                                                    ) : (
                                                                        <i className='fas fa-download fs-6' />
                                                                    )}
                                                                </button>
                                                            ) : (
                                                                <span className='text-muted'>—</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
}

export default ModalDownloadsCartao;
